import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ImportExcel } from "../components/ImportExcel";
import { FileUpload } from "../components/FileUpload";
import { PageHeader, DataTable, StatusBadge } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const EMPTY_STUDENT = { first_name: "", last_name: "", birth_date: "", gender: "M", document: "", grade: "", section: "A", school_year: "2025-2026", notes: "", photo_url: "" };
const EMPTY_GUARDIAN = { name: "", last_name: "", document: "", phone: "", whatsapp: "", email: "", address: "", relationship: "Madre" };

export default function Students() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("activo");
  const [open, setOpen] = useState(false);
  const [student, setStudent] = useState(EMPTY_STUDENT);
  const [guardian, setGuardian] = useState(EMPTY_GUARDIAN);
  const [withGuardian, setWithGuardian] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = ["superadmin", "admin", "secretaria"].includes(user?.role);

  const load = useCallback(() => {
    api.get(`/students?status=${status}`).then((r) => setStudents(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [status, formatApiError]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/r/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    try {
      const payload = { ...student };
      if (withGuardian && guardian.name) payload.guardian = guardian;
      await api.post("/students", payload);
      toast.success("Estudiante registrado");
      setOpen(false);
      setStudent(EMPTY_STUDENT);
      setGuardian(EMPTY_GUARDIAN);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = students.filter((s) =>
    !q || `${s.first_name} ${s.last_name} ${s.document}`.toLowerCase().includes(q.toLowerCase())
  );

  const columns = [
    { key: "name", label: "Estudiante", render: (s) => (
      <span className="flex items-center gap-2.5">
        {s.photo_url ? (
          <img src={s.photo_url} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" data-testid={`student-photo-${s.id}`} />
        ) : (
          <span className="w-8 h-8 rounded-full bg-blue-800/10 text-blue-800 flex items-center justify-center text-xs font-semibold">
            {s.first_name?.[0]}{s.last_name?.[0]}
          </span>
        )}
        <span className="font-medium text-slate-900">{s.first_name} {s.last_name}</span>
      </span>
    ) },
    { key: "document", label: "Documento", render: (s) => <span className="font-mono text-xs">{s.document}</span> },
    { key: "grade", label: "Curso" },
    { key: "section", label: "Sección" },
    { key: "school_year", label: "Año escolar" },
    { key: "status", label: "Estado", render: (s) => <StatusBadge value={s.status} /> },
  ];

  const setS = (k) => (e) => setStudent({ ...student, [k]: e.target.value });
  const setG = (k) => (e) => setGuardian({ ...guardian, [k]: e.target.value });

  return (
    <div data-testid="students-page">
      <PageHeader title="Estudiantes" subtitle="Fichas de estudiantes con registro de tutor">
        {canWrite && (
          <ImportExcel
            title="Importar estudiantes desde Excel"
            endpoint="/students/bulk"
            templateEndpoint="/students/template"
            templateName="plantilla_estudiantes.xlsx"
            columns={["nombre", "apellidos", "fecha_nacimiento", "sexo", "documento", "curso", "seccion", "año_escolar", "tutor_nombre", "tutor_apellido", "tutor_documento", "tutor_telefono", "tutor_whatsapp", "tutor_correo", "parentesco"]}
            onDone={load}
            testid="students"
          />
        )}
        {canWrite && (
          <Button data-testid="create-student-button" className="bg-blue-800 hover:bg-blue-700" onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Registrar estudiante
          </Button>
        )}
      </PageHeader>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="student-search-input" className="pl-9 w-72" placeholder="Buscar por nombre o documento" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="student-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="activo">Activos</SelectItem>
            <SelectItem value="retirado">Retirados</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DataTable columns={columns} rows={filtered} testid="students-table" onRowClick={(s) => navigate(`/estudiantes/${s.id}`)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Registrar estudiante</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2">Datos del estudiante</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nombre</Label><Input data-testid="student-first-name" value={student.first_name} onChange={setS("first_name")} /></div>
              <div className="space-y-1.5"><Label>Apellidos</Label><Input data-testid="student-last-name" value={student.last_name} onChange={setS("last_name")} /></div>
              <div className="space-y-1.5"><Label>Fecha de nacimiento</Label><Input type="date" data-testid="student-birth-date" value={student.birth_date} onChange={setS("birth_date")} /></div>
              <div className="space-y-1.5"><Label>Sexo</Label>
                <Select value={student.gender} onValueChange={(v) => setStudent({ ...student, gender: v })}>
                  <SelectTrigger data-testid="student-gender"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="M">Masculino</SelectItem><SelectItem value="F">Femenino</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Documento</Label><Input data-testid="student-document" value={student.document} onChange={setS("document")} /></div>
              <div className="space-y-1.5"><Label>Curso/Grado</Label>
                <Select value={student.grade} onValueChange={(v) => setStudent({ ...student, grade: v })}>
                  <SelectTrigger data-testid="student-grade"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Sección</Label>
                <Select value={student.section} onValueChange={(v) => setStudent({ ...student, section: v })}>
                  <SelectTrigger data-testid="student-section"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="A">A</SelectItem><SelectItem value="B">B</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Año escolar</Label><Input data-testid="student-school-year" value={student.school_year} onChange={setS("school_year")} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Foto del estudiante</Label>
              <div className="flex items-center gap-3">
                {student.photo_url && (
                  <img src={student.photo_url} alt="foto" className="w-12 h-12 rounded-full object-cover border border-slate-200" data-testid="student-photo-preview" />
                )}
                <FileUpload category="fotos" accept="image/*" testid="student-photo-upload" label="Subir foto"
                  onUploaded={(f) => setStudent((s) => ({ ...s, photo_url: f.url }))} />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <input type="checkbox" id="with-guardian" data-testid="with-guardian-checkbox" checked={withGuardian} onChange={(e) => setWithGuardian(e.target.checked)} />
              <Label htmlFor="with-guardian" className="!mt-0">Registrar padre/madre/tutor ahora</Label>
            </div>
            {withGuardian && (
              <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-800">Padre / Madre / Tutor</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Nombre</Label><Input data-testid="guardian-name" value={guardian.name} onChange={setG("name")} /></div>
                  <div className="space-y-1.5"><Label>Apellido</Label><Input data-testid="guardian-lastname" value={guardian.last_name} onChange={setG("last_name")} /></div>
                  <div className="space-y-1.5"><Label>Cédula</Label><Input data-testid="guardian-document" value={guardian.document} onChange={setG("document")} /></div>
                  <div className="space-y-1.5"><Label>Parentesco</Label>
                    <Select value={guardian.relationship} onValueChange={(v) => setGuardian({ ...guardian, relationship: v })}>
                      <SelectTrigger data-testid="guardian-relationship"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Madre", "Padre", "Tutor", "Abuelo/a", "Otro"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Teléfono</Label><Input data-testid="guardian-phone" value={guardian.phone} onChange={setG("phone")} /></div>
                  <div className="space-y-1.5"><Label>WhatsApp</Label><Input data-testid="guardian-whatsapp" value={guardian.whatsapp} onChange={setG("whatsapp")} /></div>
                  <div className="space-y-1.5 col-span-2"><Label>Correo</Label><Input data-testid="guardian-email" value={guardian.email} onChange={setG("email")} /></div>
                  <div className="space-y-1.5 col-span-2"><Label>Dirección</Label><Input data-testid="guardian-address" value={guardian.address} onChange={setG("address")} /></div>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-student" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="submit-student" className="bg-blue-800 hover:bg-blue-700" onClick={submit}>Registrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
